import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { getObjectLinksPath, historyLinksObjectTypes } from './history-links-routes';
import { useUserAuth } from 'features/auth';
import { fetchReq } from 'shared/api/axios';
import { useTitleHook } from 'hooks/page-title.hook';
import { INVOICE_STATUSES, CONTRACT_STATUSES, POS_STATUSES, WCC_STATUSES_MAP } from '@sr/dto';

type GraphNode = { key: string; type: string; id: number; depth: number; label: string; title: string; createdAt: string | null; path: string | null; details: Record<string, string | number | boolean> };
type Graph = { root: string; nodes: GraphNode[]; edges: { from: string; to: string; label: string }[]; truncated: boolean };

const dateLabel = (value: string | null) => value ? new Date(value).toLocaleString('ru-RU') : 'Дата создания не сохранена';
const detailLabels: Record<string, string> = {
  status: 'Статус записи', statusId: 'Код статуса', mediaplanCreatedAt: 'Медиаплан создан',
  startDate: 'Начало периода', endDate: 'Конец периода', paymentDate: 'Дата поручения',
  date: 'Дата документа', amount: 'Количество / сумма в записи', completed: 'Завершено',
};

function detailValue(node: GraphNode, key: string, value: string | number | boolean) {
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (key === 'status') {
    const maps: Record<string, Record<string, string>> = {
      invoice: INVOICE_STATUSES, contract: CONTRACT_STATUSES, payment: POS_STATUSES, wcc: WCC_STATUSES_MAP,
    };
    return maps[node.type]?.[String(value)] || String(value);
  }
  if (['mediaplanCreatedAt', 'startDate', 'endDate', 'paymentDate', 'date'].includes(key)) return dateLabel(String(value));
  return String(value);
}

// Ignore obsolete responses when navigating between object URLs.
function useHistoryData<T>(url: string | null, refresh: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    setLoading(!!url);
    if (url) fetchReq<T>(url).then((result) => {
      if (active) setData(result);
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : 'Не удалось загрузить данные');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [url, refresh]);
  return { data, error, loading };
}

export function RelationsDiagram({ graph, selected, onSelect }: { graph: Graph; selected: string | null; onSelect: (key: string) => void }) {
  const columns = [0, 1, 2].map((depth) => graph.nodes.filter((node) => node.depth === depth));
  const height = Math.max(240, ...columns.map((nodes) => nodes.length * 112 + 40));
  const positions = new Map<string, { x: number; y: number }>();
  columns.forEach((nodes, depth) => nodes.forEach((node, index) => positions.set(node.key, {
    x: 24 + depth * 350, y: 24 + index * 112,
  })));
  return (
    <Box sx={{ overflow: 'auto', maxHeight: '65vh', bgcolor: '#f2f4f8', borderRadius: 2, border: '1px solid #dce1ea' }}>
      <Box sx={{ position: 'relative', width: 1040, height }}>
        <svg width="1040" height={height} style={{ position: 'absolute', inset: 0 }} aria-label="Связи между объектами">
          {graph.edges.map((edge, index) => {
            const from = positions.get(edge.from)!;
            const to = positions.get(edge.to)!;
            const forward = from.x <= to.x;
            const x1 = from.x + (forward ? 290 : 0);
            const x2 = to.x + (forward ? 0 : 290);
            return <path key={index} d={`M${x1},${from.y + 44} C${(x1 + x2) / 2},${from.y + 44} ${(x1 + x2) / 2},${to.y + 44} ${x2},${to.y + 44}`} fill="none" stroke="#8c9bb2" strokeWidth="1.5"><title>{edge.label}</title></path>;
          })}
        </svg>
        {graph.nodes.map((node) => {
          const position = positions.get(node.key)!;
          return <Paper key={node.key} component="button" onClick={() => onSelect(node.key)} aria-pressed={selected === node.key}
            sx={{ position: 'absolute', left: position.x, top: position.y, width: 290, height: 88, p: 1.5, textAlign: 'left', cursor: 'pointer', bgcolor: 'background.paper', color: 'text.primary', border: '2px solid', borderColor: selected === node.key ? 'primary.main' : node.key === graph.root ? '#a8b5d5' : '#dce1ea', '&:focus-visible': { outline: '3px solid #748fc4' } }}>
            <Typography variant="caption" color="text.secondary">{node.label} · №{node.id}</Typography>
            <Typography noWrap fontWeight={600} title={node.title}>{node.title}</Typography>
            <Typography variant="caption" color="text.secondary">{dateLabel(node.createdAt)}</Typography>
          </Paper>;
        })}
      </Box>
    </Box>
  );
}

function HistoryLinksContent({ objectType, objectId }: { objectType: string; objectId: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const result = useHistoryData<Graph>(`history-links/${objectType}/${objectId}`, refresh);
  const graph = result.data;
  const current = graph?.nodes.find((node) => node.key === selected) || graph?.nodes.find((node) => node.key === graph.root);
  return <Stack spacing={2}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h5">История и связи</Typography>
      <Button onClick={() => setRefresh((value) => value + 1)}>Обновить</Button>
    </Stack>
    {result.loading && <CircularProgress aria-label="Построение схемы" />}
    {result.error && <Alert severity="error">{result.error}</Alert>}
    {graph && <>
      {graph.truncated && <Alert severity="warning">Показана часть связей: до 30 записей на связь и до 80 объектов. Выберите связанный объект и откройте его схему для продолжения.</Alert>}
      {graph.nodes.length === 1 && <Alert severity="info">Для этого объекта сохранённых связей не найдено среди поддерживаемых типов.</Alert>}
      <RelationsDiagram graph={graph} selected={current?.key || null} onSelect={setSelected} />
      <Typography variant="caption" color="text.secondary">На блоках указана дата создания записи. Связь через компанию не означает принадлежность к одному проекту. Платёжное поручение само по себе не подтверждает оплату.</Typography>
      {current && <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center"><Chip size="small" label={current.label} /><Typography variant="h6">{current.title}</Typography></Stack>
          <Typography variant="body2">№{current.id} · Создано: {dateLabel(current.createdAt)}</Typography>
          {Object.entries(current.details).map(([key, value]) => <Typography key={key} variant="body2">{key === 'amount' ? current.type === 'invoice-row' ? 'Количество' : 'Сумма' : detailLabels[key] || key}: {detailValue(current, key, value)}</Typography>)}
          {graph.edges.filter((edge) => edge.from === current.key || edge.to === current.key).map((edge, index) => {
            const related = graph.nodes.find((node) => node.key === (edge.from === current.key ? edge.to : edge.from))!;
            return <Button key={index} sx={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => setSelected(related.key)}>{edge.label}: {related.title}</Button>;
          })}
          <Stack direction="row" spacing={1}>
            {current.path && <Button component={Link} to={current.path}>Открыть карточку</Button>}
            {current.key !== graph.root && <Button variant="outlined" component={Link} to={getObjectLinksPath(current.type, current.id)}>Показать схему этого объекта</Button>}
          </Stack>
        </Stack>
      </Paper>}
    </>}
  </Stack>;
}

export default function HistoryLinksPage({ objectType }: { objectType?: string }) {
  useTitleHook('История и связи');
  const { user } = useUserAuth();
  const params = useParams<{ objectId: string; objectType: string }>();
  const type = objectType || params.objectType || '';
  const id = params.objectId || '';
  if (!user) return null;
  if (!user.isAdmin) return <Alert severity="error">Раздел доступен только администратору.</Alert>;
  if (!historyLinksObjectTypes.includes(type) || !/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(Number(id))) {
    return <Alert severity="error">Некорректный адрес объекта.</Alert>;
  }
  return <HistoryLinksContent key={`${type}:${id}`} objectType={type} objectId={id} />;
}
